import { Link } from '@tanstack/react-router';
import { Loader2, Phone } from 'lucide-react';

import { ContactAttemptStatusBadge } from '@apps/web/components/outreaches/contact_attempt_status_badge';
import { Badge } from '@apps/web/components/ui/badge';
import {
    formatDateAndTime,
    getDateStringInTimezone,
} from '@apps/web/lib/date_utils';
import { useClientNotificationHistory } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type ClientNotification = ApiResponseType<
    '/organizations/{organization_id}/clients/{client_id}/notification-history',
    'get'
>[number];

type ShiftSummary = ClientNotification['shifts'][number];

type ClientNotificationHistoryTabProps = {
    readonly organizationId: string;
    readonly clientId: string | null;
    readonly timezone: string | null;
    readonly onNavigateToShift?: () => void;
};

export function ClientNotificationHistoryTab({
    organizationId,
    clientId,
    timezone,
    onNavigateToShift,
}: ClientNotificationHistoryTabProps) {
    const { data: notifications, isLoading } = useClientNotificationHistory(
        organizationId,
        clientId,
        {
            reason: 'client-notification-history-tab',
        },
    );

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
        );
    }

    if (!notifications || notifications.length === 0) {
        return (
            <p className="text-muted-foreground py-8 text-center text-sm">
                No notification history
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {notifications.map((notification: ClientNotification) => (
                <NotificationCard
                    key={notification.id}
                    notification={notification}
                    timezone={timezone}
                    onNavigateToShift={onNavigateToShift}
                />
            ))}
        </div>
    );
}

function NotificationCard({
    notification,
    timezone,
    onNavigateToShift,
}: {
    readonly notification: ClientNotification;
    readonly timezone: string | null;
    readonly onNavigateToShift?: () => void;
}) {
    const tz = timezone ?? 'UTC';

    return (
        <div className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Phone className="text-muted-foreground h-3.5 w-3.5" />
                    <span className="text-sm">
                        {formatDateAndTime(notification.created_at, tz)}
                    </span>
                </div>
                <ContactAttemptStatusBadge
                    status={notification.outreach_status}
                    deliveryMode={notification.delivery_mode}
                    recipientType="client"
                />
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
                Shift assignment
                {notification.caregiver_name
                    ? ` \u2014 ${notification.caregiver_name}`
                    : ''}
            </p>
            {notification.shifts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {notification.shifts.map((shift: ShiftSummary) => (
                        <Link
                            key={shift.id}
                            to="/shifts"
                            search={{
                                date: getDateStringInTimezone(
                                    new Date(shift.shift_start_time),
                                    tz,
                                ),
                                shiftId: shift.id,
                            }}
                            onClick={onNavigateToShift}
                        >
                            <Badge
                                variant="secondary"
                                className="hover:bg-secondary/80 cursor-pointer text-xs"
                            >
                                {formatDateAndTime(shift.shift_start_time, tz)}
                            </Badge>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
